/*!
 * @copyright
 * Copyright(c) 2019 SICK AG
 */
import { Events } from '../../Events';
import { FunctionalActor } from '../../../actors/FunctionalActor';
import { Arrow } from '../AxisEditor/Arrow';
import { createLineMaterial, createDashedLineMaterial } from '../AxisEditor/createMaterials';
import { createMaterial } from '../AxisEditor/createMaterials';
import { MouseOverTracker } from '../../MouseOverTracker';
import { MousePosUtils } from '../../MousePosUtils';
import { ARROW_LENGTH, BLACK, BLUE, DEFAULT_SHAPE_EDITOR_NAME, EDITOR_SCREEN_SIZE_PX, GREEN, OPACITY_ARC, OPACITY_HIDDEN, RED, SHAPE_EDITOR_TYPE_NAME, WHITE, YELLOW, } from '../AxisEditor/constants';
import { Vector3, Raycaster } from 'three';
import { Operation, OperationDOF, GizmoMode } from '../GizmoKit/GizmoKit';
import { GizmoKitContext } from '../GizmoKit/GizmoKitContext';
import { visibleWidthAtZDepth } from '../GizmoKit/GizmoKitUtils';
import { Pointer } from '../../Pointer';
import { AbstractEditor } from './../AbstractEditor';
var EditorState;
(function (EditorState) {
    EditorState[EditorState["NeedsObject"] = 0] = "NeedsObject";
    EditorState[EditorState["Idle"] = 1] = "Idle";
    EditorState[EditorState["Rotate"] = 2] = "Rotate";
    EditorState[EditorState["Translate"] = 3] = "Translate";
    EditorState[EditorState["Scale"] = 4] = "Scale";
})(EditorState || (EditorState = {}));
const isActiveTool = (state) => state === EditorState.Rotate || state === EditorState.Translate || state === EditorState.Scale;
const ZERO = new Vector3();
/**
 * Check if the object is a shape editor or is inside a shape editor object hierarchy.
 * @param object a 3d object
 */
const isAxisEditor = (object) => {
    let obj = object;
    while (obj) {
        if (obj.type === SHAPE_EDITOR_TYPE_NAME) {
            return true;
        }
        obj = obj.parent;
    }
    return false;
};
/**
 * Simple axis based Shape Editor
 */
export class AxisEditor extends AbstractEditor {
    constructor(stage, options) {
        super(stage, options);
        this._state = EditorState.NeedsObject;
        this._transformContext = null;
        this._raycaster = new Raycaster();
        this._onCanvasMouseDown = (pos) => {
            this._mousePosition = pos;
            switch (this._state) {
                case EditorState.Idle:
                    const intersection = this._raycast(pos)[0];
                    if (intersection) {
                        intersection.object.dispatchEvent({ type: 'click', detail: { intersection, editor: this } });
                    }
                    break;
                default:
                // nothing to do here
            }
        };
        this._onCanvasMouseMove = (pos) => {
            this._mousePosition = pos;
            switch (this._state) {
                case EditorState.Idle:
                    this._mouseOverTracker.emit(this._raycast(pos).slice(0, 1));
                    break;
                case EditorState.Translate:
                    this._translate(pos);
                    break;
                case EditorState.Rotate:
                    this._rotate(pos);
                    break;
                case EditorState.Scale:
                    this._scale(pos);
                    break;
                default:
                // nothing to do here
            }
        };
        this._onCanvasMouseUp = () => {
            this.deActivateCurrentTool();
        };
        this.type = SHAPE_EDITOR_TYPE_NAME;
        this.stage = stage;
        this.name = options && options.name || DEFAULT_SHAPE_EDITOR_NAME;
        this.sizePx = options && options.sizePx || EDITOR_SCREEN_SIZE_PX;
        this._mouseOverTracker = new MouseOverTracker(this);
        this._create3dObjects();
        this._registerEventListeners();
        this._configureTools();
    }
    set _activeAxisName(axisName) {
        switch (axisName) {
            case 'xAxis':
                this._activeArrow = this._xArrow;
                break;
            case 'yAxis':
                this._activeArrow = this._yArrow;
                break;
            default: // 'zAxis'
                this._activeArrow = this._zArrow;
        }
    }
    setGizmoMode(gizmoMode) {
        this._gizmoKit.setMode(gizmoMode);
        this._configureTools();
    }
    activateRotateTool(axisName) {
        this.activateTool(Operation.Rotate, EditorState.Rotate, axisName);
    }
    activateTranslateTool(axisName) {
        this.activateTool(Operation.Translate, EditorState.Translate, axisName);
    }
    activateScaleTool(axisName) {
        this.activateTool(Operation.Scale, EditorState.Scale, axisName);
    }
    activateTool(op, toolState, axisName) {
        if (this._state === EditorState.NeedsObject) {
            return;
        }
        this.deActivateCurrentTool();
        this._state = toolState;
        this._activeAxisName = axisName;
        this.stage.pauseNavigator(true);
        const axis = (op === Operation.Rotate) ? this._activeArrow.rotateAxis : this._activeArrow.axis;
        this._transformContext = this._transformContext || new GizmoKitContext();
        this._transformContext.init(op, this._getAxisAsDof(axis));
        this._transformContext = this._gizmoKit.activate(this._transformContext, this._mousePosition);
        this._activeArrow.drawRefLine(ZERO, ZERO);
        this._activeArrow.enableRefLine();
        this._hideTools();
    }
    deActivateCurrentTool() {
        if (!isActiveTool(this._state)) {
            return;
        }
        this._gizmoKit.deactivate();
        // clear transform state ..
        this._state = EditorState.Idle;
        this._activeArrow.disableRefLine();
        this._resetTools();
        this.stage.pauseNavigator(false);
    }
    onMouseOver(intersection) {
        intersection.object.dispatchEvent({ type: 'highlight', intersection, editor: this });
    }
    onMouseOut(obj3d) {
        obj3d.dispatchEvent({ type: 'unhighlight', editor: this });
    }
    updateForObject(object) {
        this._updateSizeInRelationToCamera();
        this._configureTools();
    }
    clearObject() {
        // this._dispatchTransformEvent('endTransform');
        const old = this._object;
        this._object = null;
        this._transformContext = null;
        this._state = EditorState.NeedsObject;
        this.stage.applyThreeDirect((scene) => { scene.remove(this); });
        this._gizmoKit.setObject(null);
        this.dispatchEvent(new CustomEvent(// TODO: into the gizmoKit ???
        Events.GIZMOCHANGED, {
            detail: {
                old,
                new: null,
                editor: this,
            },
        }));
    }
    switchToObject(object) {
        if (isAxisEditor(object)) {
            // tslint:disable-next-line:no-console
            console.warn('AxisEditor: can not install shape-editor to a shape-editor hierarchy!');
            return;
        }
        const old = this._object;
        this._object = object;
        this._gizmoKit.setObject(object);
        this._gizmoKit.setCamera(this._camera);
        this.stage.applyThreeDirect((scene) => { scene.remove(this); });
        this.stage.applyThreeDirect((scene) => { scene.add(this); });
        if (this._state === EditorState.NeedsObject) {
            this._state = EditorState.Idle;
        }
        else {
            this.deActivateCurrentTool();
        }
        this.updateForObject(object);
        this.dispatchEvent(new CustomEvent(// TODO: into the gizmoKit ???
        Events.GIZMOCHANGED, {
            detail: {
                old,
                new: this._object,
                editor: this,
            },
        }));
        // this._dispatchTransformEvent('startTransform');
    }
    _hideTools() {
        this._xArrow.hideTools();
        this._yArrow.hideTools();
        this._zArrow.hideTools();
    }
    _resetTools() {
        this._xArrow.resetTools();
        this._yArrow.resetTools();
        this._zArrow.resetTools();
        this._configureTools();
    }
    _registerEventListeners() {
        this._stageDomElement.addEventListener('mousemove', (event) => this._onCanvasMouseMove(MousePosUtils.readRelMousePos(event, this._stageDomElement)));
        this._stageDomElement.addEventListener('mousedown', (event) => this._onCanvasMouseDown(MousePosUtils.readRelMousePos(event, this._stageDomElement)));
        this._stageDomElement.addEventListener('mouseup', this._onCanvasMouseUp);
        this.stage.addActor(new FunctionalActor(() => {
            if (this._state !== EditorState.NeedsObject) {
                this._updateSizeInRelationToCamera();
            }
        }));
    }
    _raycast(pos, objects = this._interactiveObjects) {
        this._raycaster.setFromCamera({ x: pos.mousex, y: pos.mousey }, this._camera);
        return this._raycaster.intersectObjects(objects, true);
    }
    _translate(pos) {
        this._gizmoKit.translate(this._transformContext, pos);
        // this._dispatchTransformEvent(`translate:${this._activeAxisName}`);
    }
    _rotate(pos) {
        const angle = this._gizmoKit.rotate(this._transformContext, pos);
        const arrow = this._activeArrow;
        const pointer = new Pointer(this._camera);
        pointer.setupBasicMode(this._transformContext.gizmoStart.position);
        arrow.drawRefLineTo(this.worldToLocal(pointer.to3DSpace(pos.mousex, pos.mousey)));
        if (this._gizmoKit.getMode() === GizmoMode.Local) {
            arrow.setRotationAngle(-angle);
        }
        // this._dispatchTransformEvent(`rotate:${this._activeAxisName}`);
    }
    _scale(pos) {
        this._gizmoKit.scale(this._transformContext, pos);
        const arrow = this._activeArrow;
        const pointer = new Pointer(this._camera);
        pointer.setupBasicMode(this._transformContext.gizmoStart.position);
        arrow.drawRefLine(this.worldToLocal(this._transformContext.surfaceMouseStart.clone()), this.worldToLocal(pointer.to3DSpace(pos.mousex, pos.mousey)));
    }
    /*private _dispatchTransformEvent(transformType: string) {
      const transformContext = this._transformContext;
  
      this.dispatchEvent({
        type: Events.GIZMOTRANSFORM,
        detail: {
          // transform,
          transformType,
          transformContext,
  
          // translate,
          // quaternion,
          // scale,
  
          editor: this,
          object: this._object,
        },
      });
    }*/
    _create3dObjects() {
        const materials = {
            extra: createMaterial({ color: YELLOW, opacity: OPACITY_ARC }),
            hidden: createMaterial({ color: BLACK, wireframe: true, opacity: OPACITY_HIDDEN }),
            refLine: createDashedLineMaterial({ color: BLACK, dashSize: 1, gapSize: 1 }),
            axisRefLine: createLineMaterial({
                color: WHITE,
                depthTest: true,
                transparent: false,
                opacity: 1,
            }),
            rotAngleStartLine: createLineMaterial({ color: YELLOW, opacity: OPACITY_ARC }),
        };
        const red = createMaterial({ color: RED });
        const green = createMaterial({ color: GREEN });
        const blue = createMaterial({ color: BLUE });
        this._xArrow = new Arrow('xAxis', new Vector3(0, 0, 1), this, Object.assign({}, materials, { normal: red }), ARROW_LENGTH, {
            rotation: [0, 0, -Math.PI / 2],
            position: [1, 0, 0],
        });
        this._yArrow = new Arrow('yAxis', new Vector3(1, 0, 0), this, Object.assign({}, materials, { normal: green }), ARROW_LENGTH, {
            rotation: [0, -Math.PI / 2, 0],
            position: [0, 1, 0],
        });
        this._zArrow = new Arrow('zAxis', new Vector3(0, 1, 0), this, Object.assign({}, materials, { normal: blue }), ARROW_LENGTH, {
            rotation: [Math.PI / 2, 0, 0],
            position: [0, 0, 1],
        });
        this.add(this._xArrow);
        this.add(this._yArrow);
        this.add(this._zArrow);
        this._interactiveObjects = [].concat(this._xArrow.interactiveObjects, this._yArrow.interactiveObjects, this._zArrow.interactiveObjects);
    }
    _getAxisAsDof(axis) {
        return axis.dot(new Vector3(1, 2, 4));
    }
    _configureTools() {
        const list = [
            { op: Operation.Translate, dof: OperationDOF.X, func: (e) => this._xArrow.enableTranslationTool(e) },
            { op: Operation.Translate, dof: OperationDOF.Y, func: (e) => this._yArrow.enableTranslationTool(e) },
            { op: Operation.Translate, dof: OperationDOF.Z, func: (e) => this._zArrow.enableTranslationTool(e) },
            { op: Operation.Rotate, dof: OperationDOF.X, func: (e) => this._yArrow.enableRotationTool(e) },
            { op: Operation.Rotate, dof: OperationDOF.Y, func: (e) => this._zArrow.enableRotationTool(e) },
            { op: Operation.Rotate, dof: OperationDOF.Z, func: (e) => this._xArrow.enableRotationTool(e) },
            { op: Operation.Scale, dof: OperationDOF.X, func: (e) => this._xArrow.enableScalingTool(e) },
            { op: Operation.Scale, dof: OperationDOF.Y, func: (e) => this._yArrow.enableScalingTool(e) },
            { op: Operation.Scale, dof: OperationDOF.Z, func: (e) => this._zArrow.enableScalingTool(e) },
        ];
        list.forEach((e) => {
            e.func(!this._gizmoKit.lockedFor(e.op, e.dof));
        });
    }
    _updateSizeInRelationToCamera() {
        if (this._state === EditorState.Idle) {
            const axes = this._gizmoKit.checkGizmoAxes();
            this._xArrow.visible = Math.abs(axes.x) < 0.98;
            this._yArrow.visible = Math.abs(axes.y) < 0.98;
            this._zArrow.visible = Math.abs(axes.z) < 0.98;
        }
        const camera = this._camera;
        const gizmo = this;
        const camPos = new Vector3();
        camera.getWorldPosition(camPos);
        const pos = new Vector3();
        gizmo.getWorldPosition(pos);
        const distance = camPos.distanceTo(pos);
        const width = visibleWidthAtZDepth(distance, camera);
        const { domElement } = this.stage.getRenderer();
        const widthPx = domElement && domElement.clientWidth || window.innerWidth;
        gizmo.scale.set(1, 1, 1).multiplyScalar(width * (this.sizePx / widthPx));
        gizmo.updateMatrix();
    }
}
//# sourceMappingURL=AxisEditor.js.map